import { useEffect, useState } from 'react';
import { Admin } from '../../api.js';
import { PageHeader } from '../../components/PageHeader.jsx';
import { Modal, ConfirmDialog } from '../../components/Modal.jsx';

const chipFor = {
  new: 'chip-warn',
  contacted: 'chip-brand',
  accepted: 'chip-success',
  rejected: 'chip-danger',
};

export default function JoinRequests() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('new');
  const [viewing, setViewing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const r = await Admin.joinRequests.list(filter || undefined);
    setItems(r.joinRequests);
  }
  useEffect(() => { refresh(); }, [filter]);

  async function accept(id) { setBusy(true); try { await Admin.joinRequests.accept(id); setViewing(null); await refresh(); } finally { setBusy(false); } }
  async function reject(id) { setBusy(true); try { await Admin.joinRequests.reject(id); setViewing(null); await refresh(); } finally { setBusy(false); } }

  return (
    <div>
      <PageHeader eyebrow="Inbox" title="Join requests" description="Review and accept new players — accepting creates a player record." />
      <div className="flex items-center gap-3 mb-5">
        <select className="input max-w-xs" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
        </select>
        <span className="text-xs text-ink-3">{items.length} {items.length === 1 ? 'request' : 'requests'}</span>
      </div>

      {items.length === 0 ? (
        <div className="card text-center py-12 text-ink-3 italic font-display">No requests.</div>
      ) : (
        <div className="space-y-3">
          {items.map((j) => (
            <article key={j.id} className="card hover:border-ink/30 transition-colors cursor-pointer" onClick={() => setViewing(j)}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-display text-xl">{j.firstName} {j.lastName}</h3>
                    <span className={chipFor[j.status] || 'chip-neutral'}>{j.status}</span>
                  </div>
                  <div className="text-sm text-ink-2">
                    {j.email}{j.phone ? ` · ${j.phone}` : ''}{j.position ? ` · ${j.position}` : ''}
                  </div>
                  {j.experience && <p className="text-sm text-ink-3 mt-2 line-clamp-2">{j.experience}</p>}
                </div>
                <span className="text-ink-3">→</span>
              </div>
            </article>
          ))}
        </div>
      )}

      <Modal
        open={!!viewing}
        onClose={() => setViewing(null)}
        eyebrow={viewing?.status || 'Request'}
        title={viewing ? `${viewing.firstName} ${viewing.lastName}` : ''}
        size="lg"
        footer={
          viewing?.status === 'new' ? (
            <>
              <button onClick={() => setConfirm({ kind: 'reject', req: viewing })} className="btn-outline text-danger">Reject</button>
              <button onClick={() => setConfirm({ kind: 'accept', req: viewing })} className="btn-primary" disabled={busy}>
                Accept & create player
              </button>
            </>
          ) : (
            <button onClick={() => setViewing(null)} className="btn-ghost">Close</button>
          )
        }
      >
        {viewing && (
          <dl className="space-y-4 text-sm">
            <Row label="Contact" value={
              <>
                <div>{viewing.email}</div>
                {viewing.phone && <div className="text-ink-2">{viewing.phone}</div>}
              </>
            }/>
            {viewing.dateOfBirth && <Row label="Date of birth" value={viewing.dateOfBirth} />}
            {viewing.position && <Row label="Position" value={viewing.position} />}
            {viewing.experience && <Row label="Experience" value={<span className="whitespace-pre-wrap">{viewing.experience}</span>} />}
            {(viewing.emergencyContactName || viewing.emergencyContactPhone) && (
              <Row label="Emergency contact" value={
                <>
                  <div>{viewing.emergencyContactName || '—'}</div>
                  {viewing.emergencyContactPhone && <div className="text-ink-2">{viewing.emergencyContactPhone}</div>}
                </>
              }/>
            )}
            {viewing.medicalNotes && <Row label="Medical notes" value={viewing.medicalNotes} />}
            <Row label="Marketing opt-in" value={viewing.marketingOptIn ? 'Yes' : 'No'} />
            <Row label="Waiver" value={viewing.waiverAccepted ? 'Accepted' : 'Not accepted'} />
          </dl>
        )}
      </Modal>

      <ConfirmDialog
        open={confirm?.kind === 'accept'}
        onClose={() => setConfirm(null)}
        onConfirm={() => accept(confirm.req.id)}
        title={`Accept ${confirm?.req?.firstName}?`}
        message="A player record will be created from this request. You can assign them to a team afterwards."
        confirmLabel="Accept"
      />
      <ConfirmDialog
        open={confirm?.kind === 'reject'}
        onClose={() => setConfirm(null)}
        onConfirm={() => reject(confirm.req.id)}
        title={`Reject ${confirm?.req?.firstName}?`}
        message="The applicant is not emailed automatically — contact them personally if appropriate."
        confirmLabel="Reject"
        danger
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-4 items-start">
      <dt className="eyebrow pt-1">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}
